import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import AuthLayout from "../../components/layouts/auth-layout";
import SideImage from "../../assets//images/sideImage.png"
import Input from "../../components/input";
import { PasswordLockIcon, SmallMailIcon } from "../../assets/icon";
import Button from "../../components/button";
import useRequest from "../../components/hook/use-req";
import { ClockLoader } from "react-spinners";
import { toast } from "sonner";
import { useGoogleLogin } from "@react-oauth/google";

// ── Google G SVG ────────────────────────────────────────────────────────────
// const GoogleIcon = () => (
//   <img
//     src="https://www.svgrepo.com/show/475656/google-color.svg"
//     alt="Google"
//     className="w-5 h-5 mr-3"
//   />
// );



// ─── Login ─────
const Login = () => {
  const { handleSubmit, control } = useForm();
  const navigate = useNavigate();
  const [viewPassword, setViewPassword] = useState(false);
  const { loading, makeRequest } = useRequest("/auth/login", "POST");
  // const { loading: googleLoader, makeRequest: googleAuth } = useRequest("/auth/google", "POST");

  const handleLogin = handleSubmit(async (formData) => {
    const login = {
      email: formData.email,
      password: formData.password,
    };

    const [response] = await makeRequest(login);

    if (response?.success) {
      const user = response?.user;
      toast.success(response?.message || 'Login successful');
      localStorage.setItem("userDetails", JSON.stringify(user));
      localStorage.setItem("authToken", response?.access_token);
      // Cached so role-gated UI (e.g. the CPaaS hub picker) can render the
      // right access state immediately instead of flashing "restricted".
      // `role` is only ever business_user/business_admin — org_role is what
      // actually distinguishes a business_user's permission level (admin,
      // conversation_agent, crm_admin, campaign_manager, marketing_manager).
      if (user?.role) {
        localStorage.setItem("role", user.role);
      }
      if (user?.org_role) {
        localStorage.setItem("org_role", user.org_role);
      }
      // global_admin and reseller each get their own dashboard/sidebar,
      // separate from the Marketing (/cpas) and CRM areas everyone else
      // lands in — reseller's is Convo Marketing pricing only (see
      // components/layouts/reseller-sidebar). Per direct ask, a "cxo"
      // role/org_role no longer skips straight to the CXO Dashboard — it
      // goes to the /cpas suite picker like every other role, so it can
      // choose CXO (or anything else it has access to) there instead.
      // Real backend value is "reseller_admin" (confirmed from an actual
      // login response), not "reseller" — checking both in case a plain
      // "reseller" role also exists.
      const isReseller = user?.role === 'reseller_admin' || user?.role === 'reseller';
      navigate(
        user?.role === 'global_admin'
          ? '/global-admin/dashboard'
          : isReseller
          ? '/reseller/dashboard'
          : '/cpas'
      )
      return;
    } else {
      toast.error(response?.detail || response?.message || 'An error occured, try again');
    }

  });

  const handleShowPassword = () => setViewPassword((prev) => !prev);

  const handleMouseDownPassword = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
  };

  // Google Sign-In handler
  // const handleGoogleSignIn = useGoogleLogin({
  //   onSuccess: async (tokenResponse) => {
  //     try {
  //       // Send the Google token to your backend
  //       const [response] = await googleAuth({
  //         access_token: tokenResponse.access_token,
  //       });

  //       if (response?.success) {
  //         const user = response?.user;
  //         toast.success(response?.message || 'Google signup successful');
  //         localStorage.setItem("userDetails", JSON.stringify(user));
  //         localStorage.setItem("authToken", response?.data?.access_token);

  //         // Check if user needs to complete profile or verify email
  //         if (response?.data?.is_new_user) {
  //           // New user - maybe redirect to complete profile
  //           navigate('/complete-profile');
  //         } else {
  //           // Existing user - redirect to dashboard
  //           navigate('/dashboard');
  //         }
  //       } else {
  //         toast.error(response?.detail?.error?.message || response.detail || 'Google signup failed');
  //       }
  //     } catch (error) {

  //       toast.error('Failed to authenticate with Google');
  //     }
  //   },
  //   flow: 'implicit', // or 'auth-code' depending on your backend setup
  // });

  return (
    <AuthLayout sideImageSrc={SideImage}>
      {/* Header */}
      <div className="mb-32">
        <h1 className="text-[32px] font-bold text-[#3A3428] dark:text-gray-100">Welcome to <span className="font-medium">ConvoHub</span></h1>
        <p className="text-[16px] text-[#434343] dark:text-gray-400">Reach customers on every channel, effortlessly.</p>
      </div>

      {/* Form */}
      <form onSubmit={handleLogin} className="space-y-4">
        <h6 className="text-[24px] font-medium text-[#3A3428] dark:text-gray-100">Sign in</h6>

        {/* Email */}
        <Controller
          name="email"
          control={control}
          defaultValue=""
          rules={{
            required: "Email is required",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Enter a valid email.",
            },
          }}
          render={({ field, fieldState }) => (
            <Input
              label="Email address"
              value={field.value}
              icon={<SmallMailIcon />}
              placeholder="Enter your email"
              className="w-full mt-2"
              error={fieldState?.error?.message}
              onChange={field.onChange}
            />
          )}
        />
        {/* Password */}

        <Controller
          name="password"
          control={control}
          rules={{
            required: "Password is required",
          }}
          render={({ field, fieldState }) => (
            <div className="w-full relative">
              <Input
                label="Password"
                icon={<PasswordLockIcon />}
                type={viewPassword ? "text" : "password"}
                value={field.value}
                className="w-full mt-4"
                onChange={field.onChange}
              />
              <button
                onClick={handleShowPassword}
                onMouseDown={handleMouseDownPassword}
                className="text-[14px] font-semibold text-[#4338CA] dark:text-indigo-400 absolute top-7 right-8 transform -translate-y-1/2 mt-4"
                type="button"
              >
                {!viewPassword ? "Show" : "Close"}
              </button>
              {fieldState?.error && (
                <p className="absolute bottom-[-18px] left-0 text-red-500 text-xs">
                  {fieldState.error.message}
                </p>
              )}
            </div>
          )}
        />

        {/* Remember me */}
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-violet-600 focus:ring-violet-500" />
            <span className="text-xs text-gray-600 dark:text-gray-400">Remember me</span>
          </label>
          <Link to="/forgot-password" className="text-xs text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 font-medium transition-colors">
            Forgot password?
          </Link>
        </div>

        {/* Submit */}
        <Button
          size="lg"
          variant="primary"
          className="w-full mt-5"
          type="submit"
        >
          {loading ? (
            <ClockLoader color="#ffffff" loading={loading} size={20} />
          ) : (
            "Sign in"
          )}

        </Button>
      </form>

      {/* Divider */}
      {/* <div className="my-5 flex items-center gap-3">
        <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
        <span className="text-[16px] text-[#434343] dark:text-gray-400 font-semibold">OR</span>
        <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
      </div> */}

      {/* Google */}
      {/* <button
        type="button"
        onClick={() => handleGoogleSignIn()}
        disabled={googleLoader}
        className="flex items-center justify-center w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium bg-white hover:bg-gray-50 hover:border-gray-400 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {googleLoader ? (
          <ClockLoader color="#4338CA" loading={googleLoader} size={20} />
        ) : (
          <>
            <GoogleIcon />
            Continue with Google
          </>
        )}
      </button> */}

      {/* Sign up */}
      {/* <p className="mt-5 text-center text-xs text-gray-500">
        Don't have an account?{" "}
        <Link to="/create-account" className="text-[#4338CA] underline hover:text-violet-700 font-semibold transition-colors">
          Create a new account
        </Link>
      </p> */}

      {/* <p className="mt-5 text-center text-xs text-gray-500 dark:text-gray-400">
        By  continuing, you agree to our {" "}
        <Link to="#" className="text-[#4338CA] dark:text-indigo-400 underline hover:text-violet-700 dark:hover:text-violet-300 font-semibold transition-colors">
          Terms
        </Link> and {" "}
        <Link to="#" className="text-[#4338CA] dark:text-indigo-400 underline hover:text-violet-700 dark:hover:text-violet-300 font-semibold transition-colors">
          Privacy Policy
        </Link>
      </p> */}
    </AuthLayout>
  );
};

export default Login;